import React, { useContext, useEffect, useState } from "react";
import {
  IonButton,
  IonButtons,
  IonContent,
  IonHeader,
  IonIcon,
  IonModal,
  IonSpinner,
  IonTitle,
  IonToolbar,
  useIonToast,
} from "@ionic/react";
import { close, copyOutline, logoApple, logoGooglePlaystore } from "ionicons/icons";
import { Clipboard } from "@capacitor/clipboard";
import { ProfileContext } from "../context/ProfileContext";
import { fx_FB_GenerateOrGetInviteCode } from "../functions/Fx_InviteCodes";
import { APP_STORE_URL_APPLE, APP_STORE_URL_GOOGLE } from "../constants/appStoreLinks";

interface ShareInviteModalProps {
  isOpen: boolean;
  onDidDismiss: () => void;
}

const buildMessage = (code: string) =>
  `I will be sending out future jobs from a new scheduling app. You will be able to take jobs, view job details, and track payments for jobs through the app. Just download the app, get registered, and enter the invite code so I can add you to all future 208 jobs.

Invite Code: ${code}

iPhone: ${APP_STORE_URL_APPLE}

Android: ${APP_STORE_URL_GOOGLE}`;

const ShareInviteModal: React.FC<ShareInviteModalProps> = ({ isOpen, onDidDismiss }) => {
  const { profile } = useContext(ProfileContext) as any;
  const [code, setCode] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>("");
  const [presentToast] = useIonToast();

  useEffect(() => {
    if (!isOpen) return;
    if (code) return; // reuse code across re-opens within same session
    if (!profile?.UserId) return;

    let cancelled = false;
    (async () => {
      setLoading(true);
      setError("");
      try {
        const generated = await fx_FB_GenerateOrGetInviteCode(
          profile.UserId,
          profile.FirstName,
          profile.LastName
        );
        if (!cancelled) setCode(generated);
      } catch (err: any) {
        if (!cancelled) setError("Could not generate an invite code. Please try again.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isOpen, profile?.UserId, profile?.FirstName, profile?.LastName, code]);

  const message = code ? buildMessage(code) : "";

  const handleCopy = async () => {
    if (!message) return;
    try {
      await Clipboard.write({ string: message });
      presentToast({
        message: "Message copied! Paste it into your group text or WhatsApp.",
        duration: 2500,
        position: "top",
        color: "success",
      });
    } catch (err) {
      // Web fallback
      try {
        await navigator.clipboard.writeText(message);
        presentToast({
          message: "Message copied!",
          duration: 2500,
          position: "top",
          color: "success",
        });
      } catch {
        presentToast({
          message: "Couldn't copy. Long-press the text to copy it manually.",
          duration: 3000,
          position: "top",
          color: "warning",
        });
      }
    }
  };

  return (
    <IonModal
      isOpen={isOpen}
      onDidDismiss={onDidDismiss}
      initialBreakpoint={0.85}
      breakpoints={[0, 0.6, 0.85, 0.95]}
    >
      <IonHeader>
        <IonToolbar>
          <IonTitle>Invite Werkers</IonTitle>
          <IonButtons slot="end">
            <IonButton onClick={onDidDismiss} aria-label="Close">
              <IonIcon slot="icon-only" icon={close} />
            </IonButton>
          </IonButtons>
        </IonToolbar>
      </IonHeader>
      <IonContent>
        <div style={{ padding: 16 }}>
          <p style={{ marginTop: 0 }}>
            Tap <strong>Copy</strong>, then paste into your group text or WhatsApp.
            Werkers enter the code when they sign up and they'll automatically be
            added to your group.
          </p>

          {loading && (
            <div style={{ textAlign: "center", padding: 24 }}>
              <IonSpinner name="crescent" />
              <div style={{ marginTop: 8, color: "var(--ion-color-medium)" }}>
                Preparing your invite code…
              </div>
            </div>
          )}

          {error && !loading && (
            <div
              style={{
                background: "var(--ion-color-danger-tint)",
                color: "var(--ion-color-danger-contrast)",
                padding: 12,
                borderRadius: 8,
                marginBottom: 12,
              }}
            >
              {error}
            </div>
          )}

          {!loading && !error && code && (
            <>
              <div
                style={{
                  border: "1px solid var(--ion-color-light-shade)",
                  borderRadius: 12,
                  padding: 12,
                  background: "var(--ion-color-light)",
                  marginBottom: 16,
                }}
              >
                <div
                  style={{
                    fontSize: 12,
                    textTransform: "uppercase",
                    letterSpacing: 0.5,
                    color: "var(--ion-color-medium)",
                    marginBottom: 4,
                  }}
                >
                  Your invite code
                </div>
                <div style={{ fontSize: 28, fontWeight: 700, letterSpacing: 2 }}>
                  {code}
                </div>
              </div>

              <pre
                style={{
                  whiteSpace: "pre-wrap",
                  wordBreak: "break-word",
                  fontFamily: "inherit",
                  fontSize: 14,
                  background: "var(--ion-color-light)",
                  border: "1px solid var(--ion-color-light-shade)",
                  borderRadius: 8,
                  padding: 12,
                  margin: 0,
                  marginBottom: 16,
                }}
              >
                {message}
              </pre>

              <IonButton
                expand="block"
                color="secondwarning"
                onClick={handleCopy}
                style={{ marginBottom: 16 }}
              >
                <IonIcon slot="start" icon={copyOutline} />
                Copy Message
              </IonButton>
              <br/>
              <br/>
              <br/>
              <br/>
              <br/>
              <br/>
              <br/>

              {/* <div style={{ display: "flex", gap: 8, marginBottom: 8 }}>
                <IonButton
                  expand="block"
                  fill="outline"
                  style={{ flex: 1 }}
                  onClick={() => window.open(APP_STORE_URL_APPLE, "_blank")}
                >
                  <IonIcon slot="start" icon={logoApple} />
                  App Store
                </IonButton>
                <IonButton
                  expand="block"
                  fill="outline"
                  style={{ flex: 1 }}
                  onClick={() => window.open(APP_STORE_URL_GOOGLE, "_blank")}
                >
                  <IonIcon slot="start" icon={logoGooglePlaystore} />
                  Play Store
                </IonButton>
              </div> */}
            </>
          )}
        </div>
      </IonContent>
    </IonModal>
  );
};

export default ShareInviteModal;
